import React from 'react';
import { motion } from 'framer-motion';
import { subDays } from 'date-fns';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiUsers, FiDollarSign, FiTrendingUp, FiTarget } = FiIcons;

const ClientsReport = ({ clients }) => {
  // Helper to calculate real growth
  const calculateGrowth = (items, valueFn = null) => {
    if (!items || items.length === 0) return { val: 0, type: 'neutral' };
    const now = new Date();
    const thirtyDaysAgo = subDays(now, 30);
    const sixtyDaysAgo = subDays(now, 60);

    const thisMonth = items.filter(i => new Date(i.createdAt) >= thirtyDaysAgo);
    const lastMonth = items.filter(i => {
      const d = new Date(i.createdAt);
      return d >= sixtyDaysAgo && d < thirtyDaysAgo;
    });

    // If valueFn provided, sum values instead of counting items
    const currentMetric = valueFn 
      ? thisMonth.reduce((sum, item) => sum + valueFn(item), 0)
      : thisMonth.length;
      
    const prevMetric = valueFn 
      ? lastMonth.reduce((sum, item) => sum + valueFn(item), 0)
      : lastMonth.length;

    if (prevMetric === 0) return { val: currentMetric > 0 ? 100 : 0, type: 'increase' };
    
    const diff = ((currentMetric - prevMetric) / prevMetric) * 100;
    return { 
      val: Math.abs(Math.round(diff)), 
      type: diff >= 0 ? 'increase' : 'decrease' 
    };
  };

  const clientStats = {
    total: clients.length,
    active: clients.filter(c => c.status === 'active').length,
    leads: clients.filter(c => c.status === 'lead').length,
    totalValue: clients.reduce((sum, c) => sum + (c.value || 0), 0),
    avgValue: clients.length > 0 ? Math.round(clients.reduce((sum, c) => sum + (c.value || 0), 0) / clients.length) : 0
  };

  const stageStats = {
    lead: clients.filter(c => c.stage === 'lead').length,
    proposal: clients.filter(c => c.stage === 'proposal').length,
    negotiation: clients.filter(c => c.stage === 'negotiation').length,
    active: clients.filter(c => c.stage === 'active' || c.status === 'active').length
  };

  const conversionRate = clientStats.leads > 0 ? Math.round((clientStats.active / clientStats.leads) * 100) : 0;
  
  // Real Growth Stats
  const totalGrowth = calculateGrowth(clients);
  const valueGrowth = calculateGrowth(clients, (c) => c.value || 0);

  const topClients = clients
    .filter(client => client.value)
    .sort((a, b) => b.value - a.value)
    .slice(0, 5);

  const renderGrowth = (growth) => (
    <div className="flex items-center mt-2">
      <span className={`text-sm font-medium ${growth.type === 'increase' ? 'text-green-600' : 'text-red-600'}`}>
        {growth.type === 'increase' ? '+' : '-'}{growth.val}%
      </span>
      <span className="text-sm text-gray-500 ml-1">vs last 30 days</span>
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Clients</p>
              <p className="text-3xl font-bold text-gray-900 mt-2">{clientStats.total}</p>
              {renderGrowth(totalGrowth)}
            </div>
            <div className="p-3 rounded-lg bg-blue-50">
              <SafeIcon icon={FiUsers} className="w-6 h-6 text-blue-600" />
            </div>
          </div>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Value</p>
              <p className="text-3xl font-bold text-gray-900 mt-2">${clientStats.totalValue.toLocaleString()}</p>
              {renderGrowth(valueGrowth)}
            </div>
            <div className="p-3 rounded-lg bg-green-50">
              <SafeIcon icon={FiDollarSign} className="w-6 h-6 text-green-600" />
            </div>
          </div>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Conversion Rate</p>
              <p className="text-3xl font-bold text-gray-900 mt-2">{conversionRate}%</p>
              <div className="flex items-center mt-2">
                <span className="text-sm text-gray-500">Lead to Active</span>
              </div>
            </div>
            <div className="p-3 rounded-lg bg-purple-50">
              <SafeIcon icon={FiTarget} className="w-6 h-6 text-purple-600" />
            </div>
          </div>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }} className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Avg Client Value</p>
              <p className="text-3xl font-bold text-gray-900 mt-2">${clientStats.avgValue.toLocaleString()}</p>
              <div className="flex items-center mt-2">
                 <span className="text-sm text-gray-500">Per Active Client</span>
              </div>
            </div>
            <div className="p-3 rounded-lg bg-yellow-50">
              <SafeIcon icon={FiTrendingUp} className="w-6 h-6 text-yellow-600" />
            </div>
          </div>
        </motion.div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Pipeline Stages */}
        <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Sales Pipeline</h3>
          <div className="space-y-4">
            {Object.entries(stageStats).map(([stage, count], index) => {
              const percentage = clientStats.total > 0 ? Math.round((count / clientStats.total) * 100) : 0;
              const stageConfig = {
                lead: { label: 'Leads', color: 'bg-yellow-500' },
                proposal: { label: 'Proposals', color: 'bg-blue-500' },
                negotiation: { label: 'Negotiations', color: 'bg-purple-500' },
                active: { label: 'Active Clients', color: 'bg-green-500' }
              };
              return (
                <div key={stage}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-gray-700">{stageConfig[stage].label}</span>
                    <span className="text-sm text-gray-600">{count} ({percentage}%)</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: `${percentage}%` }}
                      transition={{ duration: 0.8, delay: index * 0.2 }}
                      className={`h-2 rounded-full ${stageConfig[stage].color}`}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </motion.div>

        {/* Top Clients by Value */}
        <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Top Clients by Value</h3>
          <div className="space-y-4">
            {topClients.map((client, index) => (
              <div key={client.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center space-x-3">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold text-white ${
                    index === 0 ? 'bg-yellow-500' : index === 1 ? 'bg-gray-400' : index === 2 ? 'bg-amber-600' : 'bg-blue-500'
                  }`}>
                    {index + 1}
                  </div>
                  <div>
                    <h4 className="font-medium text-gray-900">{client.name}</h4>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium capitalize ${
                      client.stage === 'active' ? 'bg-green-100 text-green-800' : 
                      client.stage === 'negotiation' ? 'bg-purple-100 text-purple-800' : 
                      client.stage === 'proposal' ? 'bg-blue-100 text-blue-800' : 'bg-yellow-100 text-yellow-800'
                    }`}>
                      {client.stage}
                    </span>
                  </div>
                </div>
                <div className="text-right">
                  <div className="font-bold text-gray-900">${client.value.toLocaleString()}</div>
                </div>
              </div>
            ))}
            {topClients.length === 0 && <p className="text-gray-500 text-center py-4">No data available</p>}
          </div>
        </motion.div>
      </div>

      {/* Clients Table */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="p-6 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">All Clients</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase">Client</th>
                <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase">Stage</th>
                <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase">Value</th>
                <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase">Last Contact</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {clients.map(client => (
                <tr key={client.id}>
                  <td className="px-6 py-4">
                    <div>
                      <div className="text-sm font-medium text-gray-900">{client.name}</div>
                      <div className="text-sm text-gray-500">{client.email}</div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium capitalize ${
                      client.status === 'active' ? 'bg-green-100 text-green-800' : 
                      client.status === 'lead' ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'
                    }`}>
                      {client.status}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium capitalize ${
                      client.stage === 'active' ? 'bg-green-100 text-green-800' : 
                      client.stage === 'negotiation' ? 'bg-purple-100 text-purple-800' : 
                      client.stage === 'proposal' ? 'bg-blue-100 text-blue-800' : 'bg-yellow-100 text-yellow-800'
                    }`}>
                      {client.stage}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm font-medium text-gray-900">
                    ${(client.value || 0).toLocaleString()}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-600">
                    {client.lastContact ? new Date(client.lastContact).toLocaleDateString() : 'N/A'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </motion.div>
    </div>
  );
};

export default ClientsReport;